import React from "react";
import { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import messages from "./messages";
import { useTheme, Box, Paper, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { tokens } from "../../../assets/theme";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { svSE, enUS } from '@mui/x-data-grid/locales';
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { deleteActiveProductionOrder } from "../../../fetchers/activeProductionOrders";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import StartIcon from '@mui/icons-material/Start';
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab';
import InputOutlinedIcon from '@mui/icons-material/InputOutlined';
import { isFeatureEnabled } from "../../../hooks/useFeatureFlags";

export default function ActiveProductionOrdersDataGrid(props) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const apiRef = useGridApiRef();
    const { formatMessage } = useIntl();
    const language = localStorage.getItem('language') || 'en';
    const translations = language === "sv" ? svSE : enUS;
    const [buttonDisabled, setButtonDisabled] = React.useState(false);
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({});

    let columns = [
        {
            field: "machine",
            headerName: formatMessage(messages.machineLabel),
            flex: 1,
            align: "left",
            renderCell: (params) => (
                <>
                    {getMachineNameById(params.row.machineId)}
                </>
            ),
        },
        {
            field: "activeProductionOrderNumber",
            headerName: formatMessage(messages.productionOrderLabel),
            flex: 1,
        },
        {
            field: "activeItemId",
            headerName: formatMessage(messages.activeItemLabel),
            flex: 3,
            renderCell: (params) => (
                <>
                    {params.row.activeItemId ? params.row.activeItemId + " - " + getItemNameByItemNo(params.row.activeItemId) : ""}
                </>
            ),
        },
        {
            field: "activeTool",
            headerName: formatMessage(messages.activeToolLabel),
            flex: 3,
            renderCell: (params) => (
                <>
                    {params.row.activeTool ? params.row.activeTool + " - " + getToolNameById(params.row.activeTool) : ""}
                </>
            ),
        },
        {
            field: "idealCycleTime",
            headerName: formatMessage(messages.idealCycleTimeLabel),
            flex: 1,
        },
        {
            field: "plannedQuantity",
            headerName: formatMessage(messages.plannedQuantityLabel),
            flex: 1,
        },
        {
            field: "reportedQuantity",
            headerName: formatMessage(messages.reportedQuantityLabel),
            flex: 1,
        },
        {
            field: "remainingQuantity",
            headerName: formatMessage(messages.remainingQuantityLabel),
            flex: 1,
        },
        {
            field: "cavities",
            headerName: formatMessage(messages.cavitiesLabel),
            flex: 1,
        },
        {
            field: "edit",
            headerName: "",
            renderCell: (params) => {
                return (
                    <><Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.startLabel} /></Typography>}>
                        <Box>
                            <IconButton
                                disabled={buttonDisabled}
                                aria-label={formatMessage(messages.startLabel)}
                                size="small"
                                sx={{
                                    "&:hover": {
                                        backgroundColor: colors.primary[500],
                                    },
                                    backgroundColor: colors.primary[700],
                                }}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    props.handleEditProductionOrderClick(params.row.machineId, "start")
                                }}
                            >
                                <StartIcon fontSize="inherit" />
                            </IconButton>
                        </Box>
                    </Tooltip>
                        <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.reportLabel} /></Typography>}>
                            <Box>
                                <IconButton
                                    disabled={buttonDisabled || params.row.activeProductionOrderNumber === null}
                                    aria-label={formatMessage(messages.reportLabel)}
                                    size="small"
                                    sx={{
                                        "&:hover": {
                                            backgroundColor: colors.primary[500],
                                        },
                                        backgroundColor: colors.primary[700],
                                        marginLeft: "6px"
                                    }}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        props.handleEditProductionOrderClick(params.row.machineId, "report")
                                    }}
                                >
                                    <InputOutlinedIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                        </Tooltip>
                        <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.stopLabel} /></Typography>}>
                            <Box>
                                <IconButton
                                    disabled={buttonDisabled || params.row.activeProductionOrderNumber === null}
                                    aria-label={formatMessage(messages.stopLabel)}
                                    size="small"
                                    sx={{
                                        "&:hover": {
                                            backgroundColor: colors.primary[500],
                                        },
                                        backgroundColor: colors.primary[700],
                                        marginLeft: "6px",
                                        marginRight: "16px"
                                    }}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleDeletePO(params.row.machineId);
                                    }}
                                >
                                    <KeyboardTabIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    </>
                )
            },
        },
    ];

    const columnOrderLocalStorageName = 'prodorders-columnOrder';
    const columnWidthsLocalStorageName = 'prodorders-columnWidths';

    const handleEditProductionOrderClick = (id, action) => {
        //console.log("Edit production order with id: ", id);
        props.handleEditProductionOrderClick(id, action);
    }

    const handleDeletePO = (machineId) => {
        //console.log("[Debug] handleDeletePO - machineId: ", machineId);
        setButtonDisabled(true);
        props.setShowBackdrop(true);

        deleteActiveProductionOrder(machineId).then((result) => {

            props.setShowBackdrop(false);
            setButtonDisabled(false);

            pushNotification("Active production order deleted!");

            props.handleRefreshData();
        }).catch((error) => {
            console.log("Could not delete active production order", error);
            pushErrorMessage("Could not delete active production order: " + error?.message);
            props.setShowBackdrop(false);
            setButtonDisabled(false);
        });
    }

    const editUpdateRow = (params, event, action) => {
        // Ignore row-open when click originated from an interactive element.
        if (event?.target?.closest("button, [role='button'], a, input, textarea, select")) {
            return;
        }
        //console.log("[Debug] editUpdateRow - params: ", params);
        const id = params.row.machineId;
        handleEditProductionOrderClick(id, action);
    }

    function getMachineNameById(id) {
        const machine = props.machineQuery.data?.find((machine) => machine.id === id);
        return machine ? machine.machineName : "";
    }

    function getItemNameByItemNo(itemNo) {
        const item = props.productionItemsQuery.data?.find((item) => item.itemNo === itemNo);
        return item ? item.name : "";
    }

    function getToolNameById(id) {
        //console.log("[Debug] getToolNameById - id: ", id, ", tools: ", props.toolsQuery.data);
        const tool = props.toolsQuery.data?.find((tool) => tool.id?.toString() === id.toString());
        //console.log("[Debug] getToolNameById - found tool: ", tool);
        return tool ? tool.name : "";
    }

    // Load persisted column order, widths, and sort order on mount
    function initColumnsFromLocalStorage(columns) {
        let columnsToFix = columns;
        // Load column order
        const savedColumnOrder = localStorage.getItem(columnOrderLocalStorageName);
        if (savedColumnOrder) {
            const orderArray = JSON.parse(savedColumnOrder);
            columnsToFix = reorderColumns(orderArray, columnsToFix);
        }

        // Load column widths
        const savedWidths = localStorage.getItem(columnWidthsLocalStorageName);
        if (savedWidths) {
            const widthMap = JSON.parse(savedWidths);
            columnsToFix = applyColumnWidths(widthMap, columnsToFix);
        }
        return columnsToFix;
    };


    // Reorder columns based on saved order
    const reorderColumns = (orderArray, columnsToFix) => {
        const reorderedColumns = orderArray.map((field) =>
            columnsToFix.find((col) => col.field === field)
        );
        reorderedColumns.push(...columnsToFix.filter((col) => !reorderedColumns.includes(col)));
        return reorderedColumns;
    };

    // Apply saved column widths
    const applyColumnWidths = (widthMap, columnsToFix) => {
        const updatedColumns = columnsToFix.map((col) => ({
            ...col,
            width: widthMap[col.field] || col.width,
            flex: widthMap[col.field] !== undefined ? undefined : col.flex,
        }));
        return updatedColumns;
    };

    // Handle column order change
    const handleColumnOrderChange = () => {
        if (!apiRef.current) {
            return;
        }
        const orderedColumns = apiRef.current.getVisibleColumns();
        const newColumnOrder = orderedColumns.map((col) => col.field);
        localStorage.setItem(columnOrderLocalStorageName, JSON.stringify(newColumnOrder));
    };

    // Handle column resize
    const handleColumnResize = (params) => {
        const { colDef, width } = params;
        const newWidths = JSON.parse(localStorage.getItem(columnWidthsLocalStorageName)) || {};
        newWidths[colDef.field] = width;
        localStorage.setItem(columnWidthsLocalStorageName, JSON.stringify(newWidths));
    };

    if (props.productionOrders.isLoading) {
        return <Box padding={2}>
            <Paper
                sx={{
                    position: "relative",
                    borderRadius: "4px",
                }}
            >
                <CircularProgress color="secondary" />
            </Paper>
        </Box>
    }

    columns = initColumnsFromLocalStorage(columns);

    //add id to each row for getRowId
    let rowsWithId = props.productionOrders.data.map((row, index) => ({
        ...row,
        id: row.machineId || index, // Use existing id or fallback to index
        status: row.status || "update"
    }));

    //add machines not in active production orders with empty values to show all machines in the grid
    const machineIdsInData = props.productionOrders.data.map((row) => row.machineId);
    const machinesNotInData = props.machineQuery.data.filter((machine) => !machineIdsInData.includes(machine.id));
    const emptyRowsForMachines = machinesNotInData.map((machine) => ({
        id: machine.id,
        erpId: null,
        companyKey: companyKey,
        machineId: machine.id,
        activeProductionOrderNumber: null,
        activeTool: null,
        idealCycleTime: null,
        plannedQuantity: null,
        reportedQuantity: null,
        remainingQuantity: null,
        activeItemId: null,
        cavities: 1,
        status: "new"
    }));

    rowsWithId = [...rowsWithId, ...emptyRowsForMachines];
    rowsWithId = rowsWithId.sort((a, b) => a.machineId - b.machineId);

    //console.log("tools: ", props.toolsQuery.data);

    return (
        <Box padding={2}>
            <Paper
                sx={{
                    position: "relative",
                    borderRadius: "4px",
                }}
            >
                <Box
                    m="0px 0 0 0"
                    height="76vh"
                    sx={{
                        "& .MuiDataGrid-root": {
                            backgroundColor: colors.primary[400],
                            borderColor: `${colors.primary[700]} !important`,
                            borderRadius: "0px",
                            fontSize: "1.0rem",
                            border: "0 !important",
                        },
                        "& .MuiDataGrid-main": {
                            borderRadius: "0px",
                            border: "0 !important",
                            borderColor: `${colors.primary[700]} !important`,
                        },
                        "& .MuiDataGrid-cell": {
                            borderColor: `${colors.primary[700]} !important`,
                            borderWidth: "2px !important",
                        },
                        "& .name.column--cell": {
                            color: colors.greenAccent[300],
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: colors.primary[400],
                            fontSize: '1.0rem',
                            borderColor: `${colors.primary[700]} !important`,
                            borderWidth: "2px"
                        },
                        "& .MuiDataGrid-virtualScroller": {
                            backgroundColor: colors.primary[400],
                        },
                        "& .MuiDataGrid-footerContainer": {
                            borderTop: "none",
                            backgroundColor: colors.primary[600],
                        },
                        "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                            color: `${colors.gray[100]} !important`,
                        },
                        "& .super-app-theme--11": {
                            bgcolor: `${colors.redAccent[500]} !important`,
                        },
                        "& .super-app-theme--undefined-stop": {
                            bgcolor: `${colors.redAccent[500]} !important`,
                        },
                        "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIconButton": {
                            opacity: 0,
                            transition: "opacity 0.2s"
                        },

                        "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIconButton": {
                            opacity: 1
                        },
                    }}
                >
                    {(!props.productionOrders.isLoading) && <DataGridPro
                        apiRef={apiRef}
                        localeText={translations.components.MuiDataGrid.defaultProps.localeText}
                        onColumnResize={handleColumnResize}
                        onColumnOrderChange={handleColumnOrderChange}
                        rows={rowsWithId}
                        getRowId={(row) => row.id}
                        columns={columns}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={(newModel) =>
                            setColumnVisibilityModel(newModel)
                        }
                        onRowClick={(params, event) => editUpdateRow(params, event, "report")}
                        slotProps={{
                            panel: {
                                sx: {
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                        backgroundColor: "#799c4a",
                                        opacity: 0.3,
                                    },
                                    "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
                                        backgroundColor: "#799c4a",
                                    },
                                    "& .MuiDataGrid-panelFooter .MuiButtonBase-root": {
                                        color: colors.primary[200],
                                        fontSize: "0.75rem",
                                        "&:hover": {
                                            color: colors.primary[300],
                                        },
                                    },
                                    "& .MuiFormLabel-root.Mui-focused": {
                                        color: colors.primary[200],
                                    },
                                },
                            }
                        }}
                    />}
                    {(props.productionOrders.isLoading) && <CircularProgress color="secondary" />}
                </Box>
            </Paper>
        </Box>
    );
}